import { create, StateCreator } from "zustand";
import { persist, createJSONStorage, PersistOptions } from "zustand/middleware";

export type ILocalNotificationKeys =
  | "free-trial-1-day-end-reminder"
  | "free-trial-3-days-end-reminder"
  | "free-trial-7-days-end-reminder"
  | "free-trial-ended";

export type ILocalNotification = {
  config: {
    reminderThresholdInDays: number | null;
  };
  value: {
    read: boolean;
    lastNotified: Date | null;
  };
};

interface LocalNotificationState {
  notifications: Record<ILocalNotificationKeys, ILocalNotification>;
}

const LOCAL_NOTIFICATIONS_STORAGE_ID =
  "@sidekick:local-notifications-storage";

interface LocalNotificationActions {
  updateNotification: (
    id: ILocalNotificationKeys,
    value: ILocalNotification["value"]
  ) => void;
}

const initialState: LocalNotificationState = {
  notifications: {
    "free-trial-1-day-end-reminder": {
      config: {
        reminderThresholdInDays: 1,
      },
      value: {
        read: false,
        lastNotified: null,
      },
    },
    "free-trial-3-days-end-reminder": {
      config: {
        reminderThresholdInDays: 3,
      },
      value: {
        read: false,
        lastNotified: null,
      },
    },
    "free-trial-7-days-end-reminder": {
      config: {
        reminderThresholdInDays: 7,
      },
      value: {
        read: false,
        lastNotified: null,
      },
    },
    "free-trial-ended": {
      config: {
        reminderThresholdInDays: null,
      },
      value: {
        read: false,
        lastNotified: null,
      },
    },
  },
};

type LocalNotification = LocalNotificationState & LocalNotificationActions;

type MyPersist = (
  config: StateCreator<LocalNotification>,
  options: PersistOptions<{
    notifications: LocalNotification[];
  }>
) => StateCreator<LocalNotification>;

export const useLocalNotificationStore = create<LocalNotification>()(
  (persist as MyPersist)(
    (set) => ({
      ...initialState,
      updateNotification: (id, value) =>
        set((state) => {
          return {
            ...state,
            notifications: {
              ...state.notifications,
              [id]: {
                ...state.notifications[id],
                value,
              },
            },
          };
        }),
    }),
    {
      name: LOCAL_NOTIFICATIONS_STORAGE_ID,
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        notifications: state.notifications,
      }),
      version: 4, // Every time the schema changes this version number must be increased
    }
  )
);
